import React, { useState, useRef, useEffect } from "react";
import { popularCorporateArchitectureDesign } from "./useHook";
import Swal from "sweetalert2";

const AddPopularDesign = ({ isOpen, onClose }) => {
  const { addPopularCorporateArchitectureDesign, getAllCorporateArchitectureDesign } = popularCorporateArchitectureDesign();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [corporateArchitectureDesignData, setCorporateArchitectureDesignData] = useState([]);
  const [selectedDesigns, setSelectedDesigns] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPopularDesigns, setCurrentPopularDesigns] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const modalRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const MAX_SELECTIONS = 6;

  useEffect(() => {
    getAllCorporateArchitectureDesign((response) => {
      const designs = Array.isArray(response) ? response : response.data ? response.data : [];
      setCorporateArchitectureDesignData(designs);
    });
  }, []);

  useEffect(() => {
    if (corporateArchitectureDesignData.length > 0) {
      const popular = corporateArchitectureDesignData.filter(
        (design) => design.onHomeScreen === true
      );
      setCurrentPopularDesigns(popular);
      setSelectedDesigns(popular.map((design) => design.id));
    }
  }, [corporateArchitectureDesignData]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isDropdownOpen &&
        modalRef.current &&
        !modalRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
        setSearchQuery("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDropdownOpen]);

  useEffect(() => {
    if (isDropdownOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    } else if (!isDropdownOpen) {
      setSearchQuery("");
    }
  }, [isDropdownOpen]);

  const handleCheckboxChange = (id) => {
    setSelectedDesigns((prev) => {
      if (prev.includes(id)) {
        return prev.filter((item) => item !== id);
      }
      if (prev.length >= MAX_SELECTIONS) {
        return prev;
      }
      return [...prev, id];
    });
  };

  const removeSelectedDesign = (id) => {
    setSelectedDesigns((prev) => prev.filter((item) => item !== id));
  };

  const filteredDesigns = Array.isArray(corporateArchitectureDesignData)
    ? corporateArchitectureDesignData.filter((design) =>
      design.title
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase())
    )
    : [];

  const handleSave = async () => {
    if (selectedDesigns.length === 0) {
      alert("Please select at least one design.");
      return;
    }
    setIsSubmitting(true);
    try {
      const designData = {
        designIds: selectedDesigns,
      };
      const result = await addPopularCorporateArchitectureDesign(designData);
      if (result) {
        Swal.fire({
          title: "Success",
          text: "Popular designs saved successfully.",
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          onClose();
        });
      }
    } catch (error) {
      console.error("Error saving popular designs:", error);
      alert("Failed to save popular designs. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-800 bg-opacity-50">
      <div ref={modalRef} className="bg-white p-6 rounded-lg shadow-lg w-[600px] max-w-[90vw]">
        <h2 className="text-xl font-bold mb-4">Add/Delete Slide Show Designs</h2>
        <div className="mb-4">
          <h3 className="text-md font-bold">Already Added Designs</h3>
          <p className="text-sm text-gray-500 mb-2">
            Maximum {MAX_SELECTIONS} designs can be selected
            {selectedDesigns.length > 0 &&
              ` (${selectedDesigns.length}/${MAX_SELECTIONS} selected)`}
          </p>
          <ul className="mt-2 flex flex-wrap gap-2">
            {selectedDesigns.length > 0 ? (
              selectedDesigns.map((id) => {
                const design = corporateArchitectureDesignData.find((d) => d.id === id);
                return (
                  <li
                    key={id}
                    className="px-2 py-1 bg-blue-100 rounded-md inline-flex items-center"
                  >
                    {design?.title}
                    <button
                      onClick={() => removeSelectedDesign(id)}
                      className="ml-2 text-gray-500 hover:text-gray-700 focus:outline-none"
                      aria-label="Remove item"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  </li>
                );
              })
            ) : (
              <p>No similar designs selected</p>
            )}
          </ul>
        </div>
        <div className="relative" ref={dropdownRef}>
          <button
            className="w-full border border-gray-300 bg-white px-4 py-2 rounded-md text-left focus:outline-none"
            onClick={() => setIsDropdownOpen((prev) => !prev)}
          >
            Add more designs...
          </button>
          {isDropdownOpen && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow max-h-64 overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 p-2">
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search designs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              {filteredDesigns.length > 0 ? (
                filteredDesigns.map((design) => (
                  <label
                    key={design.id}
                    className={`cursor-pointer px-4 py-2 hover:bg-gray-100 flex items-center ${selectedDesigns.length >= MAX_SELECTIONS &&
                      !selectedDesigns.includes(design.id)
                      ? "opacity-50"
                      : ""
                      }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedDesigns.includes(design.id)}
                      onChange={() => handleCheckboxChange(design.id)}
                      disabled={
                        selectedDesigns.length >= MAX_SELECTIONS &&
                        !selectedDesigns.includes(design.id)
                      }
                      className="mr-2"
                    />
                    <span>{design.title}</span>
                  </label>
                ))
              ) : (
                <div className="px-4 py-2 text-gray-500 text-center">
                  No designs found
                </div>
              )}
            </div>
          )}
        </div>
        <div className="mt-4 flex justify-end gap-3">
          <button
            className="btn btn-reset-filter justify-center"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            className={`btn btn-primary justify-center ${
              isSubmitting ? "opacity-75 cursor-not-allowed" : ""
            }`}
            onClick={handleSave}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <svg
                  className="animate-spin h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Saving...
              </>
            ) : (
              "Save"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddPopularDesign;
